import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { kalenderApi } from '../api';

const DRINGLICHKEIT_FARBE = {
  kritisch: { bg: '#fef2f2', border: '#ef4444', text: '#dc2626', label: 'Kritisch' },
  hoch: { bg: '#fff7ed', border: '#f97316', text: '#ea580c', label: 'Hoch' },
  mittel: { bg: '#fefce8', border: '#eab308', text: '#ca8a04', label: 'Mittel' },
  niedrig: { bg: '#f0fdf4', border: '#22c55e', text: '#16a34a', label: 'Niedrig' },
  vergangen: { bg: '#f8fafc', border: '#94a3b8', text: '#64748b', label: 'Vergangen' },
};

const TYP_ICONS = {
  letzte_kuendigung: '✂️',
  vertragsende: '🏁',
  naechste_verlaengerung: '🔄',
  erinnerung_90: '🔔',
  erinnerung_30: '🔔',
  erinnerung_7: '⚠️',
  vertragsbeginn: '🚀',
};

const TYP_LABELS = {
  letzte_kuendigung: 'Letzter Kündigungstermin',
  vertragsende: 'Vertragsende',
  naechste_verlaengerung: 'Automatische Verlängerung',
  erinnerung_90: '90-Tage-Erinnerung',
  erinnerung_30: '30-Tage-Erinnerung',
  erinnerung_7: '7-Tage-Erinnerung',
  vertragsbeginn: 'Vertragsbeginn',
};

function formatDE(datum) {
  if (!datum) return '–';
  const [y, m, d] = datum.split('-');
  return `${d}.${m}.${y}`;
}

function heute() {
  return new Date().toISOString().slice(0, 10);
}

function inNTagen(n) {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

const ANSICHTEN = [
  { id: 'liste', label: 'Liste' },
  { id: 'monat', label: 'Monat' },
  { id: '30tage', label: '30 Tage' },
  { id: '90tage', label: '90 Tage' },
];

export default function KalenderPage() {
  const navigate = useNavigate();
  const [ereignisse, setEreignisse] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ansicht, setAnsicht] = useState('30tage');
  const [filter, setFilter] = useState({ typ: '', dringlichkeit: '' });
  const [aktiv, setAktiv] = useState(null);
  const [monat, setMonat] = useState(new Date().toISOString().slice(0, 7));

  const lade = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      let von, bis;
      const jetzt = heute();
      if (ansicht === '30tage') { von = jetzt; bis = inNTagen(30); }
      else if (ansicht === '90tage') { von = jetzt; bis = inNTagen(90); }
      else if (ansicht === 'monat') { von = `${monat}-01`; bis = `${monat}-31`; }
      else { von = jetzt; bis = inNTagen(365); }

      const params = { von, bis };
      if (filter.typ) params.typ = filter.typ;
      if (filter.dringlichkeit) params.dringlichkeit = filter.dringlichkeit;

      const res = await kalenderApi.ereignisse(params);
      setEreignisse(res.data.ereignisse || []);
    } catch (e) {
      setError('Kalendereignisse konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [ansicht, filter, monat]);

  useEffect(() => { lade(); }, [lade]);

  const handleIcsDownload = () => {
    const url = kalenderApi.exportIcsUrl();
    const a = document.createElement('a');
    a.href = url; a.download = 'vertragsmanager-fristen.ics'; a.click();
  };

  const gruppiertNachMonat = () => {
    const gruppen = {};
    ereignisse.forEach(e => {
      const m = e.datum ? e.datum.slice(0, 7) : 'Unbekannt';
      if (!gruppen[m]) gruppen[m] = [];
      gruppen[m].push(e);
    });
    return Object.entries(gruppen).sort(([a], [b]) => a.localeCompare(b));
  };

  const monatLabel = (m) => {
    const [y, mo] = m.split('-');
    const monate = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
      'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
    return `${monate[parseInt(mo) - 1]} ${y}`;
  };

  const card = { background: '#1e293b', borderRadius: 10, padding: 16, marginBottom: 8 };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '20px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <h1 style={{ color: '#f1f5f9', fontSize: 22, fontWeight: 700, margin: 0, flex: 1 }}>
          📅 Kalender
        </h1>
        <button
          onClick={handleIcsDownload}
          className="btn-primary"
          title="Alle Fristen als ICS-Datei herunterladen und in Outlook, Apple Kalender oder Google Kalender importieren"
        >
          📥 ICS-Datei herunterladen
        </button>
      </div>

      <p style={{ color: '#64748b', fontSize: 12, marginBottom: 16, marginTop: -12 }}>
        ICS-Datei herunterladen und in Outlook, Apple Kalender oder Google Kalender importieren.
      </p>

      {/* Ansichten */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {ANSICHTEN.map(a => (
          <button key={a.id} onClick={() => setAnsicht(a.id)}
            style={{ padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              background: ansicht === a.id ? '#818cf8' : '#1e293b',
              color: ansicht === a.id ? '#fff' : '#94a3b8' }}>
            {a.label}
          </button>
        ))}
        {ansicht === 'monat' && (
          <input type="month" value={monat} onChange={e => setMonat(e.target.value)}
            style={{ background: '#1e293b', color: '#f1f5f9', border: '1px solid #334155',
              borderRadius: 8, padding: '5px 10px', fontSize: 13 }} />
        )}
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <select value={filter.typ} onChange={e => setFilter(f => ({ ...f, typ: e.target.value }))}
          style={{ background: '#1e293b', color: '#94a3b8', border: '1px solid #334155',
            borderRadius: 8, padding: '6px 10px', fontSize: 13 }}>
          <option value="">Alle Ereignistypen</option>
          {Object.entries(TYP_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <select value={filter.dringlichkeit} onChange={e => setFilter(f => ({ ...f, dringlichkeit: e.target.value }))}
          style={{ background: '#1e293b', color: '#94a3b8', border: '1px solid #334155',
            borderRadius: 8, padding: '6px 10px', fontSize: 13 }}>
          <option value="">Alle Dringlichkeiten</option>
          {Object.entries(DRINGLICHKEIT_FARBE).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        {(filter.typ || filter.dringlichkeit) && (
          <button onClick={() => setFilter({ typ: '', dringlichkeit: '' })}
            className="btn-secondary">
            Filter zurücksetzen
          </button>
        )}
      </div>

      {loading && (
        <div style={{ color: '#6366f1', textAlign: 'center', padding: 40 }}>Kalender wird geladen…</div>
      )}
      {error && (
        <div style={{ background: '#fef2f2', color: '#dc2626', borderRadius: 10, padding: 16, marginBottom: 16 }}>
          {error} <button onClick={lade} className="btn-ghost" style={{ marginLeft: 8, textDecoration: 'underline' }}>Neu laden</button>
        </div>
      )}
      {!loading && !error && ereignisse.length === 0 && (
        <div style={{ ...card, textAlign: 'center', color: '#64748b', padding: 40 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📅</div>
          <div style={{ fontSize: 16, marginBottom: 8 }}>Keine Kalenderereignisse im gewählten Zeitraum</div>
          <div style={{ fontSize: 13 }}>Vertragsende, Kündigungstermine und Erinnerungen erscheinen hier automatisch.</div>
          <button onClick={() => navigate('/vertraege/neu')}
            className="btn-primary" style={{ marginTop: 16 }}>
            Vertrag anlegen
          </button>
        </div>
      )}

      {!loading && !error && ereignisse.length > 0 && gruppiertNachMonat().map(([m, evts]) => (
        <div key={m} style={{ marginBottom: 24 }}>
          <div style={{ color: '#818cf8', fontWeight: 700, fontSize: 15, marginBottom: 8,
            paddingBottom: 6, borderBottom: '1px solid #334155' }}>
            {monatLabel(m)}
          </div>
          {evts.map(e => {
            const farbe = DRINGLICHKEIT_FARBE[e.dringlichkeit] || DRINGLICHKEIT_FARBE.niedrig;
            const isAktiv = aktiv?.id === e.id;
            return (
              <div key={e.id}
                style={{ ...card, cursor: 'pointer', borderLeft: `4px solid ${farbe.border}`,
                  opacity: e.vergangen ? 0.65 : 1,
                  outline: isAktiv ? `2px solid ${farbe.border}` : 'none' }}
                onClick={() => setAktiv(isAktiv ? null : e)}
                role="button" tabIndex={0} aria-expanded={isAktiv}
                onKeyDown={ev => { if (ev.key === 'Enter' || ev.key === ' ') setAktiv(isAktiv ? null : e); }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <span style={{ fontSize: 20, lineHeight: 1.2 }} aria-hidden="true">
                    {TYP_ICONS[e.typ] || '📅'}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                      <span style={{ color: '#f1f5f9', fontWeight: 600, fontSize: 14 }}>
                        {e.datum_de} – {TYP_LABELS[e.typ] || e.typ}
                      </span>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 12,
                        background: farbe.bg, color: farbe.text, border: `1px solid ${farbe.border}` }}>
                        {farbe.label}
                        {!e.vergangen && e.tage_von_heute !== undefined && ` · ${e.tage_von_heute} Tage`}
                      </span>
                    </div>
                    <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 4 }}>
                      {e.vertrag_name}{e.vertragspartner ? ` · ${e.vertragspartner}` : ''}
                    </div>
                    {isAktiv && (
                      <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #334155' }}>
                        <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 8 }}>
                          {e.beschreibung}
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <button onClick={(ev) => { ev.stopPropagation(); navigate(e.url); }}
                            className="btn-primary">
                            Vertrag öffnen
                          </button>
                          <button
                            onClick={(ev) => {
                              ev.stopPropagation();
                              const token = localStorage.getItem('token');
                              const url = `/api/vertraege/${e.vertrag_id}/kalender.ics`;
                              fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                                .then(r => r.blob())
                                .then(blob => {
                                  const a = document.createElement('a');
                                  a.href = URL.createObjectURL(blob);
                                  a.download = `vertrag-${e.vertrag_id}-fristen.ics`;
                                  a.click();
                                });
                            }}
                            className="btn-secondary">
                            ICS herunterladen
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
